import { createAdminV2Error } from '@/admin-v2/foundation/errors';
import type { AdminV2ComputedResolverDefinition } from '../tool-runtime-registries';
import { resolveAdminV2RequiredSingleImage } from './required-image-input';

export const ADMIN_V2_FACE_SWAP_PROMPT = `Edit Image 1 by replacing only the facial identity of its primary person with the identity of the person in Image 2.

Image 1 is the absolute reference for the facial performance. Preserve its exact head pose, face angle, perspective, gaze direction, eyelid openness, eyebrow tension, mouth shape and openness, cheek and jaw tension, emotion, demeanor, and expression.

Image 2 is the identity reference only. Transfer the person’s recognizable facial identity and stable anatomical traits, but ignore Image 2’s expression, gaze, head pose, lighting, hairstyle, and background.

Render the person from Image 2 as if they were naturally performing the exact same expression, emotion, gaze, and head pose captured in Image 1.

Do not blend or average the two identities. The result must clearly look like the person in Image 2 while preserving the complete facial performance of Image 1.

Keep everything outside the necessary facial replacement area unchanged. Match Image 1’s lighting, skin integration, sharpness, grain, framing, and aspect ratio.

Return exactly one photorealistic edited image.`;

export const ADMIN_V2_FACE_SWAP_MATCH_INPUT_PROMPT = ADMIN_V2_FACE_SWAP_PROMPT;

const FACE_SWAP_RESOLUTION_OUTPUT_MEGAPIXELS = {
  '720p': '1',
  '1080p': '2',
  '2k': '4',
} as const;

function resolveOutputMegapixels(normalizedInput: Record<string, unknown>) {
  const resolution = normalizedInput.resolution;
  if (
    typeof resolution === 'string' &&
    resolution in FACE_SWAP_RESOLUTION_OUTPUT_MEGAPIXELS
  ) {
    return FACE_SWAP_RESOLUTION_OUTPUT_MEGAPIXELS[
      resolution as keyof typeof FACE_SWAP_RESOLUTION_OUTPUT_MEGAPIXELS
    ];
  }
  throw createAdminV2Error({
    code: 'ADMIN_V2_TOOL_FACE_SWAP_RESOLUTION_INVALID',
    message: 'Face Swap resolution must be 720p, 1080p, or 2k.',
    fieldPath: 'input.resolution',
    status: 422,
  });
}

function resolveFaceSwapImages(normalizedInput: Record<string, unknown>) {
  return [
    resolveAdminV2RequiredSingleImage({
      normalizedInput,
      fieldId: 'sourceImage',
      errorCode: 'ADMIN_V2_TOOL_FACE_SWAP_SOURCE_IMAGE_MISSING',
      message: 'Face Swap requires a photo to edit.',
    }),
    resolveAdminV2RequiredSingleImage({
      normalizedInput,
      fieldId: 'faceImage',
      errorCode: 'ADMIN_V2_TOOL_FACE_SWAP_FACE_IMAGE_MISSING',
      message: 'Face Swap requires a replacement face.',
    }),
  ];
}

function resolveFaceSwapGptImageProviderInput(
  normalizedInput: Record<string, unknown>
) {
  return {
    prompt: ADMIN_V2_FACE_SWAP_PROMPT,
    image_urls: resolveFaceSwapImages(normalizedInput),
    size: 'auto',
    resolution: '1k',
    quality: 'low',
    n: 1,
  };
}

export const faceSwapComputedResolverDefinitions = {
  faceSwapProviderInput: {
    outputType: 'json',
    resolver: ({ normalizedInput, trustedExecutionContext }) => ({
      prompt: ADMIN_V2_FACE_SWAP_MATCH_INPUT_PROMPT,
      images: resolveFaceSwapImages(normalizedInput),
      aspect_ratio: 'match_input_image',
      output_megapixels: resolveOutputMegapixels(normalizedInput),
      output_format: 'png',
      output_quality: 100,
      disable_safety_checker: trustedExecutionContext?.hasPaidAccess === true,
    }),
  },
  faceSwapGptImageOfficialProviderInput: {
    outputType: 'json',
    resolver: ({ normalizedInput }) => ({
      ...resolveFaceSwapGptImageProviderInput(normalizedInput),
      moderation: 'low',
      output_format: 'png',
    }),
  },
  faceSwapGptImageExtendedProviderInput: {
    outputType: 'json',
    resolver: ({ normalizedInput }) => ({
      ...resolveFaceSwapGptImageProviderInput(normalizedInput),
      official_fallback: true,
    }),
  },
  faceSwapFluxProviderInput: {
    outputType: 'json',
    resolver: ({ normalizedInput }) => ({
      prompt: ADMIN_V2_FACE_SWAP_PROMPT,
      images: resolveFaceSwapImages(normalizedInput),
      aspect_ratio: 'match_input_image',
      output_megapixels: '1',
      output_format: 'png',
      output_quality: 100,
      disable_safety_checker: true,
    }),
  },
} satisfies Record<string, AdminV2ComputedResolverDefinition>;
